// @flow
import { useMutation, MutationOptions } from 'react-query';
import {
  createOrUpdateAffordableApplicationAdditionalDetails,
  getAffordableApplicationAdditionalDetails,
} from 'services/affordableApplicationAdditionalDetails';
import { useContext } from 'react';
import { AppContext } from 'contexts/AppContext';
import useUserProfileQuery from 'hooks/queries/useUserProfileQuery';
import useAbortController from 'hooks/useAbortController';

type Payload = {
  applicationId: string,
  applicantId: string,
  householdId: string,
  assets: Object,
  incomes: Object,
  household: Object,
};

type Props = {
  options?: MutationOptions,
};

const useCreateOrUpdateAffordableApplicationAdditionalDetailsMutation = ({
  options,
}: Props = {}) => {
  const { organizationId, propertyId } = useContext(AppContext);
  const { refetch } = useUserProfileQuery();
  const abortControllerOptions = useAbortController();

  return useMutation(
    async ({ ...payload }: Partial<Payload>) => {
      const { data: userProfile } = await refetch();
      const { applicationId, householdId } = userProfile;
      const applicantId = payload.applicantId ?? userProfile?.applicantId;
      let id = payload.id ?? null;

      const membersAdditionalDetails =
        await getAffordableApplicationAdditionalDetails({
          id,
          organizationId,
          propertyId,
          applicationId,
          applicantId,
          householdId,
        });

      const memberAdditionalDetails = membersAdditionalDetails?.find(
        (memberDetails) => memberDetails.applicantId === applicantId,
      );

      if (memberAdditionalDetails?.id) {
        id = memberAdditionalDetails?.id;
      }

      const finalPayload: Payload = {
        ...(id && { id }),
        applicantId,
        applicationId,
        householdId,
        ...payload,
        ...(payload.assets && {
          assets: JSON.stringify({
            ...memberAdditionalDetails?.assets,
            ...payload.assets,
          }),
        }),
        ...(payload.incomes && {
          incomes: JSON.stringify({
            ...memberAdditionalDetails?.incomes,
            ...payload.incomes,
          }),
        }),
        ...(payload.household && {
          household: JSON.stringify({
            ...memberAdditionalDetails?.household,
            ...payload.household,
          }),
        }),
      };

      return createOrUpdateAffordableApplicationAdditionalDetails({
        organizationId,
        propertyId,
        payload: finalPayload,
        options: abortControllerOptions,
      });
    },
    { ...options, enabled: Boolean(propertyId && organizationId) },
  );
};

export default useCreateOrUpdateAffordableApplicationAdditionalDetailsMutation;
